// src/pages/Complaints.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { getComplaints } from '../api';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SearchPanel from '../components/SearchPanel';
import StatusBadge from '../components/StatusBadge';
import ComplaintsSortFiltersMenu from '../components/ComplaintsSortFiltersMenu';
import Icon from '../components/Icon';
import PageSizeDropdown from '../components/PageSizeDropdown';
import CustomSelect from '../components/CustomSelect';
import AnimatedEmptyState from '../components/AnimatedEmptyState';
import '../css/complaints.css';
import '../css/cards.css';
import '../css/pagination.css';
import '../css/list-summary.css';
import { pushDetailNavState } from '../utils/detailNavState';

const STORAGE_KEY = 'complaintsListState';
const COMPLAINTS_CACHE_TTL_MS = 15000;
const COMPLAINTS_CACHE_MAX_SIZE = 30;
const complaintsRequestCache = new Map();

const makeComplaintsCacheKey = (page, size, statuses, targetType, search, sort, direction) =>
  JSON.stringify({
    page,
    size,
    statuses: Array.isArray(statuses) ? [...statuses].sort() : [],
    targetType: targetType || '',
    search: search || '',
    sort,
    direction,
  });

const fetchComplaintsCached = async (page, size, statuses, targetType, search, sort, direction) => {
  const now = Date.now();
  const key = makeComplaintsCacheKey(page, size, statuses, targetType, search, sort, direction);
  const cached = complaintsRequestCache.get(key);

  if (cached?.data && now - cached.timestamp < COMPLAINTS_CACHE_TTL_MS) {
    return cached.data;
  }

  if (cached?.promise) {
    return cached.promise;
  }

  const requestPromise = getComplaints(page, size, statuses, targetType, search, sort, direction)
    .then((response) => {
      complaintsRequestCache.set(key, { data: response, timestamp: Date.now() });
      if (complaintsRequestCache.size > COMPLAINTS_CACHE_MAX_SIZE) {
        const oldestKey = complaintsRequestCache.keys().next().value;
        complaintsRequestCache.delete(oldestKey);
      }
      return response;
    })
    .catch((error) => {
      complaintsRequestCache.delete(key);
      throw error;
    });

  complaintsRequestCache.set(key, { promise: requestPromise, timestamp: now });
  return requestPromise;
};

const Complaints = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return JSON.parse(s).filters ?? { status: [], targetType: '' };
    } catch {}
    return { status: [], targetType: '' };
  });

  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return JSON.parse(s).currentPage ?? 0;
    } catch {}
    return 0;
  });

  const [paginationInfo, setPaginationInfo] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return { totalPages: 0, totalElements: 0, size: JSON.parse(s).size ?? 20 };
    } catch {}
    return { totalPages: 0, totalElements: 0, size: 20 };
  });

  const [sortParams, setSortParams] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return JSON.parse(s).sortParams ?? { sort: 'createdAt', direction: 'DESC' };
    } catch {}
    return { sort: 'createdAt', direction: 'DESC' };
  });

  const [searchValue, setSearchValue] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) return JSON.parse(s).searchValue ?? '';
    } catch {}
    return '';
  });
  const [debouncedSearch, setDebouncedSearch] = useState(searchValue);

  const topPagerRef = useRef(null);
  const shouldScrollRef = useRef(false);
  const isFirstLoad = useRef(true);
  const lastRequestRef = useRef(0);

  const locale = i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'ro' ? 'ro-RO' : 'en-US';

  const statusOptions = [
    { value: 'PENDING', label: t('complaints.statuses.PENDING') },
    { value: 'IN_REVIEW', label: t('complaints.statuses.IN_REVIEW') },
    { value: 'RESOLVED', label: t('complaints.statuses.RESOLVED') },
    { value: 'REJECTED', label: t('complaints.statuses.REJECTED') }
  ];

  const targetTypeOptions = [
    { value: '', label: t('complaints.filters.allTypes'), icon: 'layers' },
    { value: 'USER', label: t('complaints.targetTypes.USER'), icon: 'user' },
    { value: 'COMMENT', label: t('complaints.targetTypes.COMMENT'), icon: 'message-square' },
    { value: 'ORDER', label: t('complaints.targetTypes.ORDER'), icon: 'package' }
  ];

  // Persist state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      currentPage,
      filters,
      sortParams,
      size: paginationInfo.size,
      searchValue,
    }));
  }, [currentPage, filters, sortParams, paginationInfo.size, searchValue]);

  // Debounce search input before it triggers a request
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchValue);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    let cancelled = false;
    const requestId = ++lastRequestRef.current;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetchComplaintsCached(
          currentPage,
          paginationInfo.size,
          filters.status,
          filters.targetType,
          debouncedSearch,
          sortParams.sort,
          sortParams.direction
        );
        if (cancelled || requestId !== lastRequestRef.current) return;
        setComplaints(response.content || []);
        setPaginationInfo((prev) => ({
          ...prev,
          totalPages: response.totalPages || 0,
          totalElements: response.totalElements || 0,
        }));
      } catch (err) {
        if (cancelled || requestId !== lastRequestRef.current) return;
        setError(err.message || 'Failed to load complaints');
      } finally {
        if (!cancelled && requestId === lastRequestRef.current) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentPage,
    paginationInfo.size,
    filters.status,
    filters.targetType,
    debouncedSearch,
    sortParams.sort,
    sortParams.direction,
  ]);

  // Reset page on filter/sort/search change (skip first load)
  useEffect(() => {
    if (isFirstLoad.current) { isFirstLoad.current = false; return; }
    setCurrentPage(0);
  }, [filters.status, filters.targetType, debouncedSearch, sortParams.sort, sortParams.direction]);

  // Scroll to top pager after page change
  useEffect(() => {
    if (!shouldScrollRef.current || loading) return;
    const el = topPagerRef.current;
    if (!el) return;
    shouldScrollRef.current = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage, loading]);



  const handleFilterChange = (field, value) =>
    setFilters(prev => ({ ...prev, [field]: value }));

  const handleStatusChange = (newStatuses) => {
    setFilters(prev => ({ ...prev, status: newStatuses }));
  };

  const handleReset = () => {
    setFilters({ status: [], targetType: '' });
    setSortParams({ sort: 'createdAt', direction: 'DESC' });
    setSearchValue('');
    setCurrentPage(0);
  };

  const handlePageChange = (newPage, shouldScroll = false) => {
    if (newPage >= 0 && newPage < paginationInfo.totalPages) {
      if (shouldScroll) {
        shouldScrollRef.current = true;
      }
      setCurrentPage(newPage);
    }
  };

  const handleSortChange = (s) => { setSortParams(prev => ({ ...prev, sort: s })); setCurrentPage(0); };
  const handleDirectionChange = (d) => { setSortParams(prev => ({ ...prev, direction: d })); setCurrentPage(0); };


  const formatDate = (ds) => {
    if (!ds) return '';
    return new Date(ds).toLocaleString(locale, {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
    });
  };

  const getTargetTypeOption = (tt) => {
    return targetTypeOptions.find(o => o.value === tt) || { label: tt, icon: 'tag' };
  };

  // Sub-component for individual complaint card. 
  // Structure is ready for backend data (reporterName, targetTitle/Name).
  const ComplaintCard = ({ complaint, t, formatDate, getTargetTypeOption }) => {
    const targetType = getTargetTypeOption(complaint.targetType);

    // NOTE TO BACKEND: When the API is updated to return names directly in the complaint list,
    // replace the fallbacks below with complaint.reporterName and complaint.targetName.
    const reporterDisplay = complaint.reporterName || (complaint.reporterId?.substring(0, 8) || '...');
    const targetDisplay = complaint.targetName || (complaint.targetTitle || (complaint.targetId?.substring(0, 8) || '...'));

    return (
      <Link
        to={`/complaints/${complaint.id}`}
        state={pushDetailNavState(location, {})}
        className="item-card clickable-card"
      >
        <div className="card-header" style={{ 
          paddingBottom: '12px', 
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'flex-start' // Ensure icon is at the top, not centered
        }}>
          <div className="card-type-icon" style={{ 
            width: '32px', height: '32px', borderRadius: '8px', 
            background: 'rgba(59, 130, 246, 0.1)', color: 'var(--accent-primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginTop: '2px' // Subtle adjustment to align with text baseline
          }}>
            <Icon name={targetType.icon} size={16} />
          </div>
          <h4 className="card-title" style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'normal',
            fontSize: '0.95rem',
            fontWeight: 600,
            flex: 1,
            marginLeft: '12px',
            lineHeight: '1.4',
            minHeight: '4.2em'
          }}>
            {complaint.description || <span className="card-empty">—</span>}
          </h4>
        </div>

        <div className="card-body" style={{ gap: '6px', display: 'flex', flexDirection: 'column' }}>
          <div className="card-info-row" style={{ display: 'flex', gap: '4px', fontSize: '0.8rem', alignItems: 'baseline' }}>
            <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{t('complaints.labels.reporterId')}:</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {reporterDisplay}
            </span>
          </div>
          <div className="card-info-row" style={{ display: 'flex', gap: '4px', fontSize: '0.8rem', alignItems: 'baseline' }}>
            <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{targetType.label}:</span>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {targetDisplay}
            </span>
          </div>
        </div>

        <div className="card-footer">
          <StatusBadge type="complaint" status={complaint.status} />
          <div className="card-date">
            <Icon name="clock" size={12} className="card-date-icon" />
            <span>{formatDate(complaint.createdAt)}</span>
          </div>
        </div>
      </Link>
    );
  };

  // Pager logic (from Users)
  const getVisiblePages = () => {
    const total = paginationInfo.totalPages || 0;
    const cur = currentPage;
    if (total <= 1) return [];
    if (total <= 7) return Array.from({ length: total }, (_, i) => i);
    const pages = [0];
    const left = Math.max(1, cur - 1);
    const right = Math.min(total - 2, cur + 1);
    if (left > 1) pages.push('left-ellipsis');
    for (let p = left; p <= right; p++) pages.push(p);
    if (right < total - 2) pages.push('right-ellipsis');
    pages.push(total - 1);
    return pages;
  };

  const renderPager = (position = 'bottom') => {
    if (paginationInfo.totalPages <= 0) return null;
    const isFirst = currentPage <= 0;
    const isLast = currentPage >= paginationInfo.totalPages - 1;
    const pages = getVisiblePages();
    const total = paginationInfo.totalElements || 0;
    const from = total > 0 ? currentPage * paginationInfo.size + 1 : 0;
    const to = Math.min((currentPage + 1) * paginationInfo.size, total);

    return (
      <div
        className={`orders-pager orders-pager-${position}`}
        ref={position === 'top' ? topPagerRef : null}
      >
        <div className="orders-pager-info">
          {t('common.pagination.showing', { from, to, total })}
        </div>
        <div className="orders-pager-controls">
          <button type="button" className="orders-pager-nav" onClick={() => handlePageChange(currentPage - 1, position === 'bottom')} disabled={isFirst}>
            {t('common.pagination.previous')}
          </button>
          <div className="orders-pager-pages">
            {pages.map(item =>
              typeof item !== 'number' ? (
                <span key={item} className="orders-pager-ellipsis">...</span>
              ) : (
                <button
                  key={item}
                  type="button"
                  className={`orders-pager-page${item === currentPage ? ' active' : ''}`}
                  onClick={() => handlePageChange(item, position === 'bottom')}
                  aria-current={item === currentPage ? 'page' : undefined}
                >
                  {item + 1}
                </button>
              )
            )}
          </div>
          <button type="button" className="orders-pager-nav" onClick={() => handlePageChange(currentPage + 1, position === 'bottom')} disabled={isLast}>
            {t('common.pagination.next')}
          </button>
          <PageSizeDropdown
            value={paginationInfo.size}
            onChange={(newSize) => {
              setPaginationInfo(prev => ({ ...prev, size: parseInt(newSize, 10) }));
              shouldScrollRef.current = true;
              setCurrentPage(0);
            }}
            options={[10, 20, 50, 100]}
            position={position}
          />
        </div>
      </div>
    );
  };

  const filtersActive = useMemo(
    () =>
      Boolean(
        (filters.status && filters.status.length > 0) ||
          filters.targetType ||
          searchValue ||
          sortParams.sort !== 'createdAt' ||
          sortParams.direction !== 'DESC'
      ),
    [filters.status, filters.targetType, searchValue, sortParams.sort, sortParams.direction]
  );

  const extraActions = (
    <div className="complaints-extra-actions">
      <CustomSelect
        value={filters.targetType || ''}
        options={targetTypeOptions}
        onChange={(val) => handleFilterChange('targetType', val)}
        hasActiveFilter={Boolean(filters.targetType)}
      />
      <ComplaintsSortFiltersMenu
        sort={sortParams.sort}
        direction={sortParams.direction}
        onSortChange={handleSortChange}
        onDirectionChange={handleDirectionChange}
        status={filters.status}
        onStatusChange={handleStatusChange}
        targetType={filters.targetType}
        targetTypeOptions={targetTypeOptions}
        onTargetTypeChange={(val) => handleFilterChange('targetType', val)}
        onReset={handleReset}
      />
    </div>
  );

  return (
    <div className="complaints-container">
      <div className="complaints-content-surface" style={{ marginBottom: '24px' }}>
        <div className="list-summary-row">
          <div className="list-summary-header">
            <div className="list-summary-header-left">
              <div className="list-summary-icon">
                <Icon name="alert" size={24} />
              </div>
              <div>
                <h3 className="list-summary-title">{t('complaints.title')}</h3>
                <p className="list-summary-subtitle">
                  {t('home.logs.subtitle', { count: paginationInfo.totalElements })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="search-panel-wrapper">
          <SearchPanel
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            onReset={handleReset}
            loading={loading}
            placeholder={t('complaints.filters.searchPlaceholder')}
            extraActions={extraActions}
            filtersActive={filtersActive}
          />
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="complaints-content-surface">
        {/* Top pager */}
        {renderPager('top')}

        {/* Grid */}
        {loading && complaints.length === 0 ? (
          <div className="loading-state">
            <div className="strict-loader" />
            {t('complaints.loading')}
          </div>
        ) : complaints.length === 0 ? (
          <AnimatedEmptyState
            iconName="alert"
            title={t('complaints.empty.title')}
            subtitle={t('complaints.empty.subtitle')}
          />
        ) : (
          <div className="cards-grid" style={{ opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto', transition: 'opacity 0.2s' }}>
            {complaints.map(complaint => (
              <ComplaintCard 
                key={complaint.id} 
                complaint={complaint} 
                t={t} 
                formatDate={formatDate} 
                getTargetTypeOption={getTargetTypeOption} 
              />
            ))}
          </div>
        )}

        {/* Bottom pager */}
        {renderPager('bottom')}
      </div>
    </div>
  );
};

export default Complaints;